import { FC, ReactNode, useMemo, useState } from 'react'
import type { BoxProps } from '@mui/material/Box'
import type { StackProps } from '@mui/material/Stack'
import { Outlet, useParams, useMatches } from 'react-router-dom'
import { Page } from '../../../components/Page'
import { SidePanelSkeleton } from './EngagementSidePanel/SidePanelStates'
import { useEngagement, useEngagementActivities } from '../../../api/apiQueries'
import { Skeleton } from '../../../components/Skeleton/Skeleton'
import { EngagementInfoBox } from './EngagementInfoBox/EngagementInfoBox'
import { ModeType, isModeRouteHandle } from './EngagementDetailsEnums'
import { Banner, BannerType } from '../../../components/Banner/Banner'
import { PageOutletContext } from '../../../hooks/useSidePanelOutletContext'

// Default full-bleed layout, used by any mode other than Inspect/Customer Care.
const FULL_SCROLL_CONTAINER_PROPS = { sx: { backgroundColor: 'var(--bg-surface)' } }
const FULL_MAX_WIDTH_CONTAINER_PROPS = { sx: { maxWidth: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 } }

// Customer Care has no side panel, so the body is centered at a readable width instead of spanning
// the full layout the other modes use for their video player and side panel.
const CUSTOMER_CARE_PAGE_BODY_PROPS: BoxProps = { sx: { width: '100%', maxWidth: '1160px', margin: '0 auto' } }
const CUSTOMER_CARE_SCROLL_CONTAINER_PROPS = {}
const CUSTOMER_CARE_MAX_WIDTH_CONTAINER_PROPS: BoxProps = { sx: { maxWidth: '100%' } }

export const ErtPage: FC = () => {
  const { engagementId } = useParams()
  const currentHandle = useMatches()
    .map(match => match.handle)
    .find(isModeRouteHandle)
  const currentMode = currentHandle?.mode ?? ModeType.QA
  const { data: engagement, isLoading: engagementIsLoading, isFetched: engagementIsFetched } = useEngagement(engagementId)
  const { data: activities, isLoading: activitiesIsLoading } = useEngagementActivities(engagementId)

  const [sidePanelAddOn, setSidePanelAddOn] = useState<ReactNode>(null)
  // setSlots is a stable useState setter but still need to memoize the context object
  const outletContext = useMemo<PageOutletContext>(() => ({ setSidePanelAddOn }), [])

  const isInspect = currentMode === ModeType.Inspect
  const isCustomerCare = currentMode === ModeType.CustomerCare

  let pageBodyProps: BoxProps
  let scrollContainerProps: StackProps
  let maxWidthContainerProps: BoxProps
  if (isCustomerCare) {
    pageBodyProps = CUSTOMER_CARE_PAGE_BODY_PROPS
    scrollContainerProps = CUSTOMER_CARE_SCROLL_CONTAINER_PROPS
    maxWidthContainerProps = CUSTOMER_CARE_MAX_WIDTH_CONTAINER_PROPS
  } else {
    pageBodyProps = isInspect
      ? { sx: { flex: 1, minHeight: 0, minWidth: 0, width: '100%', display: 'flex', flexDirection: 'column' } }
      : { height: '100%', width: '100%' }
    scrollContainerProps = FULL_SCROLL_CONTAINER_PROPS
    maxWidthContainerProps = FULL_MAX_WIDTH_CONTAINER_PROPS
  }

  return (
    <Page
      showLocationSearchBar={true}
      headerAddOn={
        engagement && activities ? (
          <Skeleton isLoading={engagementIsLoading || activitiesIsLoading} sx={{ minHeight: '3.5rem' }}>
            {engagementId && <EngagementInfoBox engagement={engagement} activities={activities} currentMode={currentMode} />}
          </Skeleton>
        ) : (
          engagementIsFetched &&
          !engagement &&
          !isCustomerCare && (
            <Banner
              data-testid="missing-engagement-banner"
              type={BannerType.Warning}
              title="We are unable to load the status bar and additional engagement details. This is expected for all engagements prior to April 2026."
            />
          )
        )
      }
      sidePanelAddOn={sidePanelAddOn ?? (currentMode === ModeType.CustomerCare ? null : <SidePanelSkeleton />)}
      {...pageBodyProps}
      scrollContainerProps={scrollContainerProps}
      maxWidthContainerProps={maxWidthContainerProps}
    >
      <Outlet context={outletContext} />
    </Page>
  )
}
